/**
 * The drawing half of the validation rail — and nothing else.
 *
 * Both documents' rails now draw **one flat list of what is missing**, and this
 * frame is why they cannot drift apart:
 *
 *  - the **goods DeCA** used to apportion blame between Cargador and
 *    Transportista, because the €401 penalty attaches per liable party. Their
 *    testers asked for the split to go on 2026-09-18 — see `ValidationSummary`,
 *    which still shows the penalty notice, because the penalty did not go with
 *    it.
 *  - the **passenger route sheet** never had one. FOM/1230/2013 has no such
 *    split and the carrier undertaking answers for the sheet as a whole (client
 *    answer 1, 2026-09-09), so there was nothing to group by and no penalty to
 *    warn about. That is `PassengerValidationSummary`.
 *
 * `ValidationGroup` therefore carries no heading, and a caller cannot give it
 * one: the two things that must stay apart are blocking failures and warnings,
 * which is what the groups are now for. Printing a party split onto a route
 * sheet — an assertion about who is liable, made by the portal rather than by
 * the law — stopped being expressible here rather than merely being avoided.
 *
 * This frame has no opinion about parties, penalties or line items. It takes a
 * title that already names the document that cannot be generated, and a footer
 * the caller may or may not have.
 */

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { FieldError } from '@deca/api'

import { Notice } from './primitives.tsx'

export interface ValidationGroup {
  blocking: FieldError[]
  warnings: FieldError[]
}

export interface ValidationSummaryFrameProps {
  groups: ValidationGroup[]
  /**
   * The banner over the blocking failures. Required, and deliberately not
   * defaulted: the goods wording says «No se puede generar el DeCA», which is
   * the wrong document — and the wrong legal instrument — on a route sheet's
   * rail. A default here would make that mistake the silent one.
   */
  blockingTitle: string
  /**
   * The line-item pointer for one failure, worded by the rail that owns it:
   * "Envío 2" for a grouped DeCA's consignment, "Servicio 2" for a grouped
   * sheet's service. The two contracts name the field differently on purpose
   * (`consignmentSeq` / `servicioSeq`), which is exactly why the frame does not
   * read it itself.
   */
  seqNote?: (field: FieldError) => ReactNode
  /** Rendered when there is nothing wrong. */
  okMessage?: string
  compact?: boolean
  /** Shown under the blocking failures, on the same terms as the banner. */
  footer?: ReactNode
}

export function ValidationSummaryFrame({
  groups,
  blockingTitle,
  seqNote,
  okMessage,
  compact,
  footer,
}: ValidationSummaryFrameProps) {
  const { t } = useTranslation()

  const blockingCount = groups.reduce((total, group) => total + group.blocking.length, 0)
  const allWarnings = groups.flatMap((group) => group.warnings)

  if (blockingCount === 0 && allWarnings.length === 0) {
    return okMessage ? <Notice tone="success">{okMessage}</Notice> : null
  }

  return (
    <div className="stack-sm">
      {/* `compact` is the import review, where every failed row would otherwise
          repeat the same banner and the same €401 paragraph. The row's status
          chip already says it failed and the screen explains the penalty once,
          so a row shows only *what* is missing. */}
      {blockingCount > 0 && !compact ? (
        <Notice tone="danger" title={blockingTitle}>
          <span>{t('errors.validationBody', { count: blockingCount })}</span>
        </Notice>
      ) : null}

      {groups.map((group, groupIndex) => {
        if (group.blocking.length === 0) return null
        return (
          <div key={groupIndex} className="stack-sm">
            <ul className="stack-sm" style={{ margin: 0, paddingLeft: 'var(--space-lg)' }}>
              {group.blocking.map((field, index) => {
                const note = seqNote?.(field)
                return (
                  <li key={`${field.field}-${index}`} className="small">
                    {/* labelEs is what the document prints — kept verbatim, in
                        either UI language, so the user recognises the field on
                        the PDF they are about to produce. */}
                    <strong>{field.labelEs}</strong>
                    {note ? <span className="muted"> · {note}</span> : null}
                    {!compact ? <span className="muted"> — {field.message}</span> : null}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}

      {blockingCount > 0 && !compact ? footer : null}

      {allWarnings.length > 0 ? (
        <Notice tone="warning" title={t('create.warningsTitle')}>
          <span className="small">{t('create.warningsHelp')}</span>
          <ul className="stack-sm" style={{ margin: 0, paddingLeft: 'var(--space-lg)' }}>
            {allWarnings.map((field, index) => (
              <li key={`${field.field}-${index}`} className="small">
                <strong>{field.labelEs}</strong> — {field.message}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </div>
  )
}
